import { type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataGrid, type DataGridProps, gridClasses } from "@mui/x-data-grid";

import CustomDataGridToolbar from "./Toolbar/CustomDataGridToolbar";

// This is a shallow wrapper of the Mui DataGrid
// to allow consistent default styling throughout the app

interface DefaultDataGridProps extends DataGridProps {
  title?: string;
  showToolbar?: boolean;
  refetch?: () => void;
  onAdd?: () => void;
  disableCellFocus?: boolean;
  clickableRow?: boolean;
  loading: boolean;
  wrapHeaders?: boolean;
  sx?: SxProps<Theme>;
  disableZebraStripes?: boolean;
}

// Conditional style objects //
// This removes the outline when you click in a cell
// Useful if for instance you want to navigate on a row click
const cellFocusStyles = {
  "& .MuiDataGrid-cell:focus": {
    outline: "none",
  },
  "& .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
};

const ODD_OPACITY = 0.2;
// Base styles shared by all DataGrid variants
const baseDataGridStyles = {
  ".MuiDataGrid-cell": {
    padding: "1em",
    display: "flex",
    alignItems: "center",
  },
  ".MuiDataGrid-columnSeparator": {
    display: "none",
  },
  ".MuiDataGrid-iconButtonContainer > .MuiIconButton-root:hover:hover": {
    backgroundColor: "inherit",
  },
  ".MuiTablePagination-toolbar > p": {
    marginBottom: "initial",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    lineHeight: "1.25em",
    padding: ".5em",
    fontWeight: "bold",
  },
};

// Striped row styling (alternating even rows)
const stripedDataGridStyles = (theme: Theme) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
});

// This lets users know a row is clickable
const clickableRowStyles = {
  "& .MuiDataGrid-row": {
    cursor: "pointer",
  },
};

const wrapHeadersStyles = {
  "& .MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal",
    lineHeight: "normal",
  },
  "& .MuiDataGrid-columnHeader": {
    height: "auto !important",
  },
};

// If we declare the toolbar inside of the component we lose a stable reference to it
// which means it rerenders every time the table fetches
// This is where the bug where it loses focus on refetches comes from
// It is declared here as a simple pass through to get around this limitation
// NOTE: using memoization or callbacks will not get around this as they will change on the rows changing
// We need a truly stable reference to an instance of this component which is created here and passed in below
const CustomToolbar = (props: any) => <CustomDataGridToolbar {...props} />;

const DefaultDataGrid = ({
  rows,
  columns,
  title,
  showToolbar = true,
  refetch,
  onAdd,
  disableCellFocus = false,
  clickableRow = false,
  wrapHeaders = true,
  sx = {},
  rowCount = undefined,
  disableZebraStripes = false,
  ...props
}: DefaultDataGridProps) => {
  return (
    <DataGrid
      disableColumnMenu
      disableDensitySelector
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnSelector
      getRowHeight={() => "auto"}
      getRowId={(row: any) => row.id}
      rows={rows}
      rowCount={rowCount}
      columns={columns}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
      }
      slots={{
        toolbar: showToolbar ? CustomToolbar : undefined,
      }}
      slotProps={{
        toolbar: {
          title,
          refetch,
          onAdd,
          isServerSide: props.paginationMode === "server",
          rowCountOverride: rowCount,
        } as any, // I cant figure out how to get it to recognize custom components like this so i am using an any cast here :(
      }}
      sx={[
        baseDataGridStyles,
        disableZebraStripes ? {} : stripedDataGridStyles,
        disableCellFocus ? cellFocusStyles : {},
        clickableRow ? clickableRowStyles : {},
        wrapHeaders ? wrapHeadersStyles : {},
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    />
  );
};
export default DefaultDataGrid;
